import React, { useState, useEffect, createContext, useContext } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, Sparkles, X } from 'lucide-react'
import SiteBuilder from '@/pages/SiteBuilder'
import ProjectPreviewPage from '@/pages/ProjectPreviewPage'

type AuthContextType = {
  accessToken: string | null
  setAccessToken: (token: string) => void
  setModalOpen: (open: boolean) => void
}

const AuthContext = createContext<AuthContextType>({
  accessToken: null,
  setAccessToken: () => { },
  setModalOpen: () => { },
})

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

function setCookie(name: string, value: string, days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessTokenState] = useState<string | null>(() => getCookie('brixel_access_token'))
  const [modalOpen, setModalOpen] = useState(false)
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formMode, setFormMode] = useState<'login' | 'register'>('login')
  const [register, setRegister] = useState({ firstname: '', lastname: '', email: '', password: '' })
  const [registerLoading, setRegisterLoading] = useState(false)
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [registerSuccess, setRegisterSuccess] = useState(false)

  function setAccessTokenPersist(token: string | null) {
    setAccessTokenState(token)
    if (!token) setCookie('brixel_access_token', '', -1)
    else setCookie('brixel_access_token', token)
  }

  // Reset modal on open
  useEffect(() => {
    if (modalOpen) {
      setLoginError(null)
      setEmail('')
      setPassword('')
      setFormMode('login')
      setRegister({ firstname: '', lastname: '', email: '', password: '' })
      setRegisterError(null)
      setRegisterSuccess(false)
    }
  }, [modalOpen])

  // Login
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoginLoading(true)
    setLoginError(null)
    try {
      const res = await fetch('https://api.brixel.ai/backoffice/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || 'Erreur de connexion')
      }
      const data = await res.json()
      if (data.access_token) {
        setAccessTokenPersist(data.access_token)
        setModalOpen(false)
      } else {
        throw new Error('Token manquant')
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Erreur inconnue')
    } finally {
      setLoginLoading(false)
    }
  }

  // Register
  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setRegisterLoading(true)
    setRegisterError(null)
    setRegisterSuccess(false)
    try {
      const res = await fetch('https://api.brixel.ai/backoffice/user/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(register),
      })
      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || 'Erreur lors de la création du compte')
      }
      setRegisterSuccess(true)
    } catch (err: any) {
      setRegisterError(err?.message || 'Erreur inconnue')
    } finally {
      setRegisterLoading(false)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        setAccessToken: setAccessTokenPersist,
        setModalOpen
      }}
    >
      {children}
      {modalOpen && (
        <div className="fixed inset-0 z-[20000] bg-black/40 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 40 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-5 relative"
          >
            <button
              className="absolute top-3 right-3 rounded-full p-2 text-gray-400 hover:text-brixel focus:outline-none focus:ring-2 focus:ring-brixel"
              onClick={() => {
                setModalOpen(false)
              }}
              aria-label="Fermer"
              type="button"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center mb-2">
              <img src="https://cdn.brixel.ai/logo/brixel_logo_small.png" alt="Brixel" className="h-8 w-8 mr-2" draggable={false} />
              <span className="text-lg font-bold text-brixel">Connexion requise</span>
            </div>

            <div className="flex border-b mb-3">
              <button
                className={`flex-1 py-2 text-center font-semibold transition ${formMode === 'login' ? 'border-b-2 border-brixel text-brixel bg-gray-50' : 'text-gray-500 hover:text-brixel'}`}
                onClick={() => setFormMode('login')}
                type="button"
              >
                Se connecter
              </button>
              <button
                className={`flex-1 py-2 text-center font-semibold transition ${formMode === 'register' ? 'border-b-2 border-brixel text-brixel bg-gray-50' : 'text-gray-500 hover:text-brixel'}`}
                onClick={() => setFormMode('register')}
                type="button"
              >
                Créer un compte
              </button>
            </div>

            {/* LOGIN FORM */}
            {formMode === 'login' && (
              <form onSubmit={handleLogin} className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full border px-3 py-2 rounded"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Mot de passe</label>
                  <input
                    type="password"
                    className="w-full border px-3 py-2 rounded"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                </div>
                {loginError && <div className="text-red-500 text-sm">{loginError}</div>}
                <button
                  type="submit"
                  className="btn w-full flex items-center justify-center"
                  disabled={loginLoading}
                >
                  {loginLoading ? 'Connexion…' : 'Se connecter'}
                </button>
              </form>
            )}

            {/* REGISTER FORM */}
            {formMode === 'register' && (
              <div>
                {!registerSuccess ? (
                  <form onSubmit={handleRegister} className="flex flex-col gap-4">
                    <div className="flex gap-2">
                      <div>
                        <label className="block text-sm font-semibold mb-1">Prénom</label>
                        <input type="text" className="w-full border px-3 py-2 rounded"
                          value={register.firstname}
                          onChange={e => setRegister(r => ({ ...r, firstname: e.target.value }))}
                          required
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Nom</label>
                        <input type="text" className="w-full border px-3 py-2 rounded"
                          value={register.lastname}
                          onChange={e => setRegister(r => ({ ...r, lastname: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Email</label>
                      <input type="email" className="w-full border px-3 py-2 rounded"
                        value={register.email}
                        onChange={e => setRegister(r => ({ ...r, email: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Mot de passe</label>
                      <input type="password" className="w-full border px-3 py-2 rounded"
                        minLength={8}
                        value={register.password}
                        onChange={e => setRegister(r => ({ ...r, password: e.target.value }))}
                        required
                        placeholder="Au moins 8 caractères"
                      />
                    </div>
                    {registerError && <div className="text-red-500 text-sm">{registerError}</div>}
                    <button
                      type="submit"
                      className="btn w-full flex items-center justify-center"
                      disabled={registerLoading}
                    >
                      {registerLoading ? 'Création...' : 'Créer le compte'}
                    </button>
                  </form>
                ) : (
                  // Affichage après création de compte
                  <div className="text-center py-6 flex flex-col items-center">
                    <span className="text-brixel text-4xl mb-2">📧</span>
                    <div className="font-semibold text-lg mb-2">Vérification requise</div>
                    <p className="mb-2 text-gray-700">
                      Un email de confirmation vient d&apos;être envoyé à <br />
                      <span className="font-mono break-all">{register.email}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Merci de cliquer sur le lien contenu dans cet email pour activer votre compte.
                    </p>
                    <button
                      className="mt-4 underline text-brixel font-semibold"
                      type="button"
                      onClick={() => setFormMode('login')}
                    >
                      Revenir à la connexion
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AuthContext.Provider>
  )
}
// --- Auth hook ---
export function useAuth() {
  return useContext(AuthContext)
}

// --- Patch fetch for API calls (SiteBuilder, ProjectPreviewPage, CodeUpdater) ---

// Helper: returns a fetch wrapper that ensures auth before calling
export function useAuthFetch() {
  const { accessToken, setModalOpen, setAccessToken } = useAuth()
  return async (input: RequestInfo, init: RequestInit = {}) => {
    // Ajoute le token si présent
    const headers = new Headers(init.headers || {});
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    const response = await fetch(input, { ...init, headers });

    if (response.status === 401) {
      setAccessToken(null);
      setModalOpen(true);
      throw new Error('Votre session a expiré ou vous n\'êtes pas authentifié.');
    }
    return response;
  }
}

// --- LOGO URLs ---
const LOGO_URL = 'https://cdn.brixel.ai/logo/brixel_logo_full.png'
const LOGO_SMALL_URL = 'https://cdn.brixel.ai/logo/brixel_logo_small.png'

// --- LandingPage (unchanged) ---
function LandingPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="container flex items-center gap-4 py-6">
        <img
          src={LOGO_URL}
          alt="Brixel SiteBuilder"
          className="h-8 w-auto"
          draggable={false}
        />
        <span className="text-xl font-bold text-brixel tracking-tight">SiteBuilder</span>
        <span className="ml-auto">
          <Link
            to="/sitebuilder"
            className="inline-flex items-center gap-2 rounded-lg bg-brixel px-4 py-2 text-base font-semibold text-white shadow-md transition hover:bg-brixel/90 focus:outline-none focus:ring-2 focus:ring-brixel focus:ring-offset-2 hover:underline hover:text-white"
          >
            <Sparkles className="w-5 h-5" />
            Accéder à l’éditeur
          </Link>
        </span>
      </header>
      <main className="flex-1 flex flex-col justify-center">
        <section className="container flex flex-col-reverse lg:flex-row items-center gap-12 py-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="w-full max-w-xl text-center lg:text-left"
          >
            <h1 className="mb-4 text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
              Créez votre site <span className="text-brixel">simplement</span>
            </h1>
            <p className="mb-8 text-lg text-gray-600 md:text-xl">
              SiteBuilder Brixel : créez, organisez et prévisualisez facilement votre site web multi-pages
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <Link
                to="/sitebuilder"
                className="inline-flex items-center rounded-lg bg-brixel px-6 py-3 text-base font-semibold text-white shadow-md transition hover:bg-brixel/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-brixel focus:ring-offset-2"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Commencer maintenant
              </Link>
              <a
                href="#features"
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-6 py-3 text-base font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brixel focus:ring-offset-2"
              >
                Découvrir les fonctionnalités
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full max-w-lg flex-shrink-0"
          >
            <img
              src="https://cdn.brixel.ai/images/site_builder.jpg"
              alt="Illustration SiteBuilder"
              className="w-full h-auto drop-shadow-xl pointer-events-none select-none"
              draggable={false}
              loading="eager"
            />
          </motion.div>
        </section>
        <section id="features" className="container py-12 md:py-20">
          <h2 className="mb-8 text-center text-2xl font-bold text-gray-900 md:text-3xl">
            Fonctionnalités principales
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-blue-brixel" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M3 9h18" /></svg>
              }
              title="Pages modulaires"
              desc="Ajoutez, organisez et naviguez entre vos pages en un clin d’œil."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-yellow-brixel" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17h11a2.5 2.5 0 0 1 2.5 2.5V21H4v-1.5ZM6.5 17V5.5A2.5 2.5 0 0 1 9 3h6a2.5 2.5 0 0 1 2.5 2.5V17" /></svg>
              }
              title="Composants personnalisés"
              desc="Créez et décrivez vos propres composants pour chaque page ou globalement."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-green-brixel" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 3v2m6.364 1.636-1.414 1.414M21 12h-2M17.364 17.364l-1.414-1.414M12 21v-2M6.636 17.364l1.414-1.414M3 12h2M6.636 6.636l1.414 1.414" /><circle cx="12" cy="12" r="5" /></svg>
              }
              title="Prévisualisez votre projet généré"
              desc="Visualisez le site crée directement depuis l'interface"
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-pink-500" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M13.4126 15L10.5421 10.9335C10.0849 10.2858 9.85636 9.96204 9.57524 9.85289C9.32941 9.75744 9.05583 9.76296 8.81405 9.86825C8.53756 9.98865 8.32224 10.3214 7.89161 10.9869L4.25737 16.6035C4.16197 16.7509 4.11427 16.8247 4.08048 16.9041C4.0505 16.9746 4.02872 17.0483 4.01557 17.1238C4.00075 17.2088 4.00073 17.2967 4.00068 17.4723L4 20H20.0008L20.0008 17.4604C20.0008 17.2891 20.0008 17.2035 19.9867 17.1204C19.9742 17.0467 19.9534 16.9746 19.9249 16.9055C19.8926 16.8276 19.8471 16.7551 19.7561 16.61L18.79 15.0702C17.9362 13.7095 17.5093 13.0291 16.9545 12.7811C16.4695 12.5642 15.9181 12.5492 15.4221 12.7395C14.8547 12.957 14.3915 13.6132 13.4651 14.9256L13.4126 15ZM13.4126 15L16.9545 20M20.0008 6C20.0008 7.10457 19.1054 8 18.0008 8C16.8962 8 16.0008 7.10457 16.0008 6C16.0008 4.89543 16.8962 4 18.0008 4C19.1054 4 20.0008 4.89543 20.0008 6Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g>
                </svg>
              }
              title="Import de visuels"
              desc="Ajoutez des images et illustrations pour décrire vos pages et composants."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 10.5C8 11.3284 7.32843 12 6.5 12C5.67157 12 5 11.3284 5 10.5C5 9.67157 5.67157 9 6.5 9C7.32843 9 8 9.67157 8 10.5Z" fill="#0F1729"></path> <path d="M10.5 8C11.3284 8 12 7.32843 12 6.5C12 5.67157 11.3284 5 10.5 5C9.67157 5 9 5.67157 9 6.5C9 7.32843 9.67157 8 10.5 8Z" fill="#0F1729"></path> <path d="M17 6.5C17 7.32843 16.3284 8 15.5 8C14.6716 8 14 7.32843 14 6.5C14 5.67157 14.6716 5 15.5 5C16.3284 5 17 5.67157 17 6.5Z" fill="#0F1729"></path> <path d="M7.5 17C8.32843 17 9 16.3284 9 15.5C9 14.6716 8.32843 14 7.5 14C6.67157 14 6 14.6716 6 15.5C6 16.3284 6.67157 17 7.5 17Z" fill="#0F1729"></path> <path fill-rule="evenodd" clip-rule="evenodd" d="M1 12C1 5.92487 5.92487 1 12 1C17.9712 1 23 5.34921 23 11V11.0146C23 11.543 23.0001 12.4458 22.6825 13.4987C21.8502 16.2575 18.8203 16.9964 16.4948 16.4024C16.011 16.2788 15.5243 16.145 15.0568 16.0107C14.2512 15.7791 13.5177 16.4897 13.6661 17.2315L13.9837 18.8197L14.0983 19.5068C14.3953 21.289 13.0019 23.1015 11.0165 22.8498C7.65019 22.423 5.11981 21.1007 3.43595 19.1329C1.75722 17.171 1 14.6613 1 12ZM12 3C7.02944 3 3 7.02944 3 12C3 14.2854 3.64673 16.303 4.95555 17.8326C6.25924 19.3561 8.3 20.4894 11.2681 20.8657C11.7347 20.9249 12.2348 20.4915 12.1255 19.8356L12.0163 19.1803L11.7049 17.6237C11.2467 15.3325 13.4423 13.4657 15.6093 14.0885C16.0619 14.2186 16.529 14.3469 16.9897 14.4646C18.7757 14.9208 20.3744 14.2249 20.7677 12.921C20.997 12.161 21 11.5059 21 11C21 6.65079 17.0745 3 12 3Z" fill="#0F1729"></path> </g>
                </svg>
              }
              title="Personnalisation du style"
              desc="Adaptez les couleurs, polices et dispositions pour créer un site à votre image."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path d="M4 4v5h5" />
                  <path d="M5 19a9 9 0 1 0-1-9" />
                </svg>
              }
              title="Mises à jour versionné"
              desc="Continuez de demander des modifications une fois le projet généré et conservez la liste des versions générées"
            />
          </div>
        </section>
        <section className="container py-12 md:py-20 border-t mt-8" id="about-brixel">
          <h2 className="mb-4 text-center text-2xl font-bold text-brixel md:text-3xl">
            Qu&apos;est-ce que Brixel&nbsp;?
          </h2>
          <p className="text-lg text-gray-700 text-center max-w-2xl mx-auto">
            Brixel est un orchestrateur IA conçu pour piloter et déléguer l’exécution de centaines de tâches métier personnalisables, propres à votre entreprise. Donnez-lui une instruction en langage naturel, il construit automatiquement un plan d’action, orchestre les étapes,  puis délègue leur exécution à vos propres systèmes et environnements, garantissant ainsi la confidentialité de vos données et le contrôle total de vos processus.<br />
            <span className="block mt-2 font-bold text-brixel-dark">Ce site vous propose un exemple concret de ce que Brixel peut accomplir, en mettant l’IA au service de vos processus métier.</span>
          </p>
        </section>
      </main>
      <footer className="border-t bg-white py-8 text-center text-sm text-gray-500">
        <div className="container flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2">
            <img
              src={LOGO_SMALL_URL}
              alt="Brixel"
              className="h-6 w-6"
              draggable={false}
            />
            <span>
              © {new Date().getFullYear()} Brixel SiteBuilder
            </span>
          </div>
          <div>
            <a
              href="https://brixel.ai"
              className="text-brixel hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              brixel.ai
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

function FeatureCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.5 }}
      className="rounded-xl bg-white p-6 shadow-md ring-1 ring-gray-100 hover:shadow-lg transition"
    >
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-50">
        {icon}
      </div>
      <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600">{desc}</p>
    </motion.div>
  )
}

function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -24 }}
        transition={{ duration: 0.25 }}
        className="min-h-screen flex flex-col"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

function BackBar() {
  return (
    <nav className="container flex items-center gap-2 py-4">
      <Link
        to="/"
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-base font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brixel focus:ring-offset-2"
      >
        <ArrowLeft className="w-5 h-5 text-brixel" />
        Retour à l’accueil
      </Link>
    </nav>
  )
}

// --- App wrapper with AuthProvider ---
export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/"
          element={
            <PageTransition>
              <LandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/sitebuilder"
          element={
            <PageTransition>
              <BackBar />
              <SiteBuilder />
            </PageTransition>
          }
        />
        <Route
          path="/sitebuilder/preview"
          element={
            <PageTransition>
              <ProjectPreviewPage />
            </PageTransition>
          }
        />
        <Route
          path="*"
          element={
            <PageTransition>
              <div className="flex flex-col items-center justify-center min-h-screen">
                <img
                  src={LOGO_SMALL_URL}
                  alt="Brixel"
                  className="h-12 w-12 mb-6"
                  draggable={false}
                />
                <h1 className="text-3xl font-bold text-brixel mb-2">404 – Page introuvable</h1>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brixel px-4 py-2 text-base font-semibold text-white shadow-md transition hover:bg-brixel/90 focus:outline-none focus:ring-2 focus:ring-brixel focus:ring-offset-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Retour à l’accueil
                </Link>
              </div>
            </PageTransition>
          }
        />
      </Routes>
    </AuthProvider>
  )
}
